/**
 * Currency display configuration
 *
 * Options for the currency picker. Static list matches supported product
 * currencies (CAD, USD, MXN). MXN is a guest view/pay currency only — host
 * payouts stay CAD/USD (TENANT-5645). Labels are translated: render them with
 * `formatCurrencyOptionLabel` / `formatCurrencyOptionSublabel` (copy lives in
 * `currency-config.messages.*.js`). To apply a user’s choice, use `bookingStore.applySelectedCurrencyCode`
 * (`src/stores/booking-store.js`); that validates against this set and against API
 * `supportedCurrencies` when loaded.
 */

import { isMexicanHostVisit } from './locale-config.js'
import { messages } from './currency-config.messages.en.js'

/**
 * @typedef {object} CurrencySelectOption
 * @property {string} code - ISO 4217 code (e.g. CAD, USD)
 * @property {string} symbol - currency symbol shown beside the code
 * @property {{ id: string, defaultMessage: string }} labelMessage - full name, translated
 */

// TODO: Use API `supportedCurrencies` to build this list instead of hardcoding?
/** @type {CurrencySelectOption[]} */
export const CURRENCY_SELECT_OPTIONS = [
    {
        code: 'CAD',
        labelMessage: messages.CAD,
        symbol: '$',
    },
    {
        code: 'USD',
        labelMessage: messages.USD,
        symbol: '$',
    },
    {
        code: 'MXN',
        labelMessage: messages.MXN,
        symbol: '$',
    },
]

/**
 * Primary line of a currency option (its full name) in the viewer's locale.
 *
 * @param {{ formatMessage: Function }} intl
 * @param {CurrencySelectOption} option
 * @returns {string}
 */
export function formatCurrencyOptionLabel (intl, option) {
    return intl.formatMessage(option.labelMessage)
}

/**
 * Secondary line of a currency option (code and symbol) in the viewer's locale.
 *
 * @param {{ formatMessage: Function }} intl
 * @param {CurrencySelectOption} option
 * @returns {string}
 */
export function formatCurrencyOptionSublabel (intl, option) {
    return intl.formatMessage(messages.optionSublabel, {
        code: option.code,
        symbol: option.symbol,
    })
}

export const SUPPORTED_CURRENCY_CODES = new Set(CURRENCY_SELECT_OPTIONS.map((option) => option.code))

/** Currency a lodgr.mx visitor sees when they have no saved currency preference. */
export const MEXICAN_HOST_DEFAULT_CURRENCY = 'MXN'

/**
 * Currency implied by the visit's host when no saved preference exists
 * (lodgr.mx → MXN, incl. `?simulateHost=lodgr.mx`). Independent of the locale:
 * the host implies a default for each, and changing one never changes the other.
 * Not written to localStorage.
 *
 * @param {{ hostname: string, search?: string }} [location] - defaults to `window.location`
 * @returns {string | null}
 */
export function getHostDefaultCurrency (location = window.location) {
    if (isMexicanHostVisit({
        hostname: location.hostname,
        search: location.search,
    })) {
        return MEXICAN_HOST_DEFAULT_CURRENCY
    }

    // eslint-disable-next-line unicorn/no-null -- null means "no host-implied currency" (distinct from a resolved code)
    return null
}

/**
 * Whether a code may become the selected currency: product-supported and, once
 * the API list has loaded (non-empty), offered by the API too.
 *
 * @param {unknown} code
 * @param {string[]} apiSupportedCodes - codes from API `supported_currencies`; empty when not loaded
 * @returns {boolean}
 */
export function isSelectableCurrencyCode (code, apiSupportedCodes) {
    if (!code || typeof code !== 'string' || !SUPPORTED_CURRENCY_CODES.has(code)) {
        return false
    }
    if (Array.isArray(apiSupportedCodes) && apiSupportedCodes.length > 0) {
        return apiSupportedCodes.includes(code)
    }
    return true
}

/**
 * The currency a visit starts in. Precedence, highest first: the saved
 * preference, then the host-implied default, then the API/base default. A
 * candidate that is not selectable is skipped.
 *
 * @param {object} candidates
 * @param {string} candidates.baseCode - API `default_currency` (or the store's base code)
 * @param {string | null} candidates.hostDefaultCode - from {@link getHostDefaultCurrency}
 * @param {string | null} candidates.savedCode - persisted `currency_preference`
 * @param {string[]} candidates.apiSupportedCodes - API `supported_currencies` codes; empty when not loaded
 * @returns {string}
 */
export function resolveInitialCurrencyCode ({
    baseCode,
    hostDefaultCode,
    savedCode,
    apiSupportedCodes,
}) {
    if (isSelectableCurrencyCode(savedCode, apiSupportedCodes)) {
        return savedCode
    }
    if (isSelectableCurrencyCode(hostDefaultCode, apiSupportedCodes)) {
        return hostDefaultCode
    }
    return baseCode
}

/**
 * Whether saving the language/currency picker should apply and persist the picked
 * currency. Language and currency are independent (TENANT-5645): only a currency
 * the user actually changed is saved, so a language-only save never pins the
 * currency the visit merely started in — e.g. a lodgr.mx visitor's host-implied
 * MXN — as a saved preference.
 *
 * @param {string} pickedCode - currency selected in the picker
 * @param {string} effectiveCode - currency the visit is currently in
 * @returns {boolean}
 */
export function shouldPersistCurrencySelection (pickedCode, effectiveCode) {
    return pickedCode !== effectiveCode
}

/**
 * @param {string} code
 * @returns {CurrencySelectOption | undefined}
 */
export function getCurrencySelectOption (code) {
    return CURRENCY_SELECT_OPTIONS.find((option) => option.code === code)
}

/**
 * Ordered options for codes present in both the static config and the given list
 * (e.g. API `supportedCurrencies`). Preserves `CURRENCY_SELECT_OPTIONS` order.
 *
 * @param {string[]} codes
 * @returns {CurrencySelectOption[]}
 */
export function getCurrencySelectOptionsForCodes (codes) {
    const codeSet = new Set(codes)
    return CURRENCY_SELECT_OPTIONS.filter((option) => codeSet.has(option.code))
}
